import { createContext, useContext, useState, useEffect } from 'react';

type ColorScheme = 'light' | 'dark';

interface ThemeContextType {
  isDark: boolean;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextType>({ isDark: false, toggle: () => {} });

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [isDark, setIsDark] = useState<boolean>(() => {
    const stored = localStorage.getItem('app-color-scheme');
    if (stored === 'dark') return true;
    if (stored === 'light') return false;
    // Default: system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Stamp the HTML element immediately so BrandTheme can read it. We toggle
  // BOTH `data-color-scheme="dark"` (the attribute existing CSS rules in
  // index.css key off — `[data-color-scheme="dark"] .brand-ahp .terminal-card`
  // etc.) AND the `.dark` class (Tailwind's `darkMode: ["class"]` selector).
  // Without the class toggle, every `dark:` Tailwind variant in the codebase
  // silently no-ops (ShareDignityModal's dark gradient, autocomplete
  // dark-mode bg, ~30 other dark: instances Ciel identified 2026-05-25).
  // Keeping both selectors means existing CSS rules and Tailwind variants
  // fire from the same toggle, no per-component refactor needed.
  useEffect(() => {
    const html = document.documentElement;
    if (isDark) {
      html.setAttribute('data-color-scheme', 'dark');
      html.classList.add('dark');
    } else {
      html.removeAttribute('data-color-scheme');
      html.classList.remove('dark');
    }
    localStorage.setItem('app-color-scheme', isDark ? 'dark' : 'light');
  }, [isDark]);

  const toggle = () => setIsDark(prev => !prev);

  return (
    <ThemeContext.Provider value={{ isDark, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
